import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Rocket, X } from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { useSessao } from '@/hooks/useSessao';
import { cn, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';

interface Resposta {
  emissao: number;
  pedidos: number;
  etiquetas: number;
}

/**
 * Emitir a onda — o único botão do WMS que escreve no ciclo do pedido.
 *
 * Pede confirmação e diz exatamente o que vai acontecer, porque nada disso se
 * desfaz por aqui: as etiquetas nascem, o estoque das posições fica reservado
 * para as próximas ondas e os pedidos vão para o estado 3. Cancelar é assunto
 * do Procfit.
 *
 * Fica na barra da onda, e não na aba de etiquetas, porque emitir é a
 * conclusão da MONTAGEM: quem terminou de escolher os pedidos está com a
 * decisão na mão, em qualquer aba.
 */
export function EmitirOnda({
  empresa,
  televendas,
  etiquetas,
  sku,
  aoEmitir,
}: {
  empresa: number;
  televendas: readonly number[];
  /** Quantas etiquetas a prévia calculou — para a confirmação dizer o tamanho. */
  etiquetas: number;
  /** SKU do filtro da onda, quando houver: vai para o cabeçalho da emissão. */
  sku: number | null;
  aoEmitir: (emissao: number) => void;
}): JSX.Element | null {
  const { temPermissao } = useSessao();
  const cliente = useQueryClient();
  const [aberto, definirAberto] = React.useState(false);

  const emitir = useMutation<Resposta, unknown, void>({
    mutationFn: () =>
      api.post<Resposta>(
        `/api/separacao/emitir?empresa=${empresa}`,
        sku === null ? { televendas } : { televendas, produto: sku },
      ),
    onSuccess: (r) => {
      // A fila elegível e a lista de ondas mudaram: estes pedidos saíram de uma
      // e entraram na outra.
      void cliente.invalidateQueries({ queryKey: ['separacao'] });
      definirAberto(false);
      aoEmitir(r.emissao);
    },
  });

  if (!temPermissao('separacao.emitir')) return null;

  const erro = emitir.error;
  /** A regra recusou pedido a pedido: a lista diz QUAIS e por quê. */
  const impedidos =
    erro instanceof ErroHttp && Array.isArray(erro.detalhes)
      ? (erro.detalhes as { televenda: number; motivo: string }[])
      : [];

  return (
    <Dialog.Root
      open={aberto}
      onOpenChange={(v) => {
        definirAberto(v);
        if (!v) emitir.reset();
      }}
    >
      <Dialog.Trigger asChild>
        <Botao variante="primaria" tamanho="pequeno" disabled={televendas.length === 0}>
          <Rocket className="h-3.5 w-3.5" />
          Emitir onda
        </Botao>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/45 backdrop-blur-[2px] data-[state=open]:animate-aparecer" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(520px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-borda bg-superficie shadow-forte data-[state=open]:animate-escalar">
          <div className="flex items-start justify-between gap-3 border-b border-borda bg-superficie2 px-5 py-3.5">
            <div>
              <Dialog.Title className="text-[16px] font-bold tracking-[-0.01em]">
                Emitir esta onda?
              </Dialog.Title>
              <Dialog.Description className="mt-0.5 text-xs text-textoFraco">
                A gravação acontece no ERP e não se desfaz pelo WMS.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Botao variante="fantasma" tamanho="icone" aria-label="Fechar">
                <X className="h-5 w-5" />
              </Botao>
            </Dialog.Close>
          </div>

          <div className="space-y-3 px-5 py-4">
            <p className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="leading-none">
                <b className="tabular text-[24px] font-bold text-primaria">
                  {numero(televendas.length)}
                </b>
                <span className="rotulo ml-1.5">
                  {televendas.length === 1 ? 'pedido' : 'pedidos'}
                </span>
              </span>
              <span className="leading-none">
                <b className="tabular text-[24px] font-bold">{numero(etiquetas)}</b>
                <span className="rotulo ml-1.5">etiquetas</span>
              </span>
              <span className="text-[12px] text-textoFraco">empresa {empresa}</span>
            </p>

            <ol className="grid gap-1 text-[12.5px] text-textoFraco">
              <li>1. grava a onda e os pedidos escolhidos no ERP;</li>
              <li>
                2. gera <b className="text-texto">uma etiqueta por unidade</b> — e cada etiqueta
                pendente <b className="text-texto">reserva o saldo</b> daquela posição para as
                próximas ondas;
              </li>
              <li>
                3. move os pedidos para o <b className="text-texto">estado 3</b>.
              </li>
            </ol>

            {erro ? (
              <div className="rounded-lg border border-status-divergencia/30 bg-status-divergenciaFundo px-3 py-2 text-[12.5px]">
                <p className="flex items-center gap-1.5 font-semibold text-status-divergencia">
                  <AlertTriangle className="h-4 w-4" />
                  {erro instanceof Error ? erro.message : 'Não foi possível emitir.'}
                </p>
                {impedidos.length > 0 ? (
                  <ul className="mt-1.5 grid gap-0.5 text-textoFraco">
                    {impedidos.slice(0, 8).map((i) => (
                      <li key={i.televenda}>
                        <b className="tabular font-mono text-texto">{i.televenda}</b> — {i.motivo}
                      </li>
                    ))}
                    {impedidos.length > 8 ? <li>e mais {impedidos.length - 8}…</li> : null}
                  </ul>
                ) : null}
                <p className="mt-1.5 text-textoFraco">Nada foi gravado.</p>
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-borda bg-superficie2 px-5 py-3">
            <Dialog.Close asChild>
              <Botao variante="secundaria" tamanho="pequeno">
                Cancelar
              </Botao>
            </Dialog.Close>
            <Botao
              variante="primaria"
              tamanho="pequeno"
              carregando={emitir.isPending}
              onClick={() => emitir.mutate()}
              className={cn(emitir.isPending && 'pointer-events-none')}
            >
              <Rocket className="h-4 w-4" />
              Emitir {numero(etiquetas)} etiquetas
            </Botao>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
